import React, { useEffect, useState } from 'react';
import { Loader2, PenLine, Type, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SignaturePad from './SignaturePad';
import { fileToDataUri, typedSignatureDataUri } from '../utils/signatureImage';
import { toast } from 'sonner';

export default function SignatureCaptureModal({
  open, onOpenChange, savedImage, doctorName, onConfirm, busy,
}) {
  const [tab, setTab] = useState('draw');
  const [drawn, setDrawn] = useState('');
  const [typed, setTyped] = useState(doctorName || '');
  const [typedPreview, setTypedPreview] = useState('');
  const [uploaded, setUploaded] = useState('');
  const [saveProfile, setSaveProfile] = useState(true);

  useEffect(() => {
    if (!open) return;
    setTyped(doctorName || '');
    setDrawn('');
    setUploaded('');
    setTab(savedImage ? 'saved' : 'draw');
  }, [open, savedImage, doctorName]);

  useEffect(() => {
    if (!open) return;
    const uri = typedSignatureDataUri(typed);
    setTypedPreview(uri);
  }, [open, typed]);

  const onUpload = async (e) => {
    try {
      setUploaded(await fileToDataUri(e.target.files?.[0]));
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
  };

  const activeImage = () => {
    if (tab === 'saved') return savedImage || '';
    if (tab === 'draw') return drawn || '';
    if (tab === 'type') return typedPreview || '';
    if (tab === 'upload') return uploaded || '';
    return '';
  };

  const confirm = () => {
    const image = activeImage();
    if (!image?.startsWith('data:image')) {
      toast.error(
        tab === 'type'
          ? 'Enter a name to preview your typed signature'
          : 'Please create or select a signature first',
      );
      return;
    }
    onConfirm({ signatureImage: image, saveToProfile: saveProfile });
  };

  const previewSrc = activeImage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#071B34]">Attach signature</DialogTitle>
          <DialogDescription>
            Your signature appears on the printed letterhead. Save it for future referrals.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`grid w-full ${savedImage ? 'grid-cols-4' : 'grid-cols-3'}`}>
            {savedImage ? <TabsTrigger value="saved">Saved</TabsTrigger> : null}
            <TabsTrigger value="draw"><PenLine className="mr-1 h-3.5 w-3.5" />Draw</TabsTrigger>
            <TabsTrigger value="type"><Type className="mr-1 h-3.5 w-3.5" />Type</TabsTrigger>
            <TabsTrigger value="upload"><Upload className="mr-1 h-3.5 w-3.5" />Upload</TabsTrigger>
          </TabsList>

          {savedImage && (
            <TabsContent value="saved" className="pt-3">
              <p className="mb-2 text-xs text-stone-500">Using your saved signature.</p>
            </TabsContent>
          )}
          <TabsContent value="draw" className="pt-3">
            <SignaturePad onChange={setDrawn} />
          </TabsContent>
          <TabsContent value="type" className="space-y-3 pt-3">
            <div>
              <Label htmlFor="typed-sig-name">Name or initials</Label>
              <Input
                id="typed-sig-name"
                className="mt-1"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="e.g. Anil Kumar"
                data-testid="typed-signature-input"
              />
            </div>
            <p className="text-[11px] text-stone-500">
              Preview updates as you type — this is how it will print on the letter.
            </p>
          </TabsContent>
          <TabsContent value="upload" className="pt-3">
            <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} />
          </TabsContent>
        </Tabs>

        <div
          className="rounded-xl border border-dashed border-[#D5DEE8] bg-[#F8FAFC] px-4 py-5"
          data-testid="signature-preview-panel"
        >
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7A8B9C]">
            Signature preview
          </p>
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Signature preview"
              className="mx-auto max-h-28 max-w-full object-contain"
              data-testid="signature-preview-image"
            />
          ) : (
            <p className="py-6 text-center text-sm text-stone-400">
              {tab === 'draw' && 'Draw above to see your signature here'}
              {tab === 'type' && 'Type a name to see the script preview'}
              {tab === 'upload' && 'Upload an image to preview it here'}
              {tab === 'saved' && 'No saved signature'}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-stone-600">
          <input type="checkbox" checked={saveProfile} onChange={(e) => setSaveProfile(e.target.checked)} />
          Remember this signature for next time
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="bg-[#071B34] text-white" onClick={confirm} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Attach &amp; issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
